interface NestedFilter {
  [key: string]:
    | string
    | string[]
    | number
    | boolean
    | NestedFilter
    | NestedFilter[];
}

export interface QueryOptions {
  populate?: string | string[] | Record<string, string[]>;
  fields?: string[];
  pagination?: {
    page?: number;
    pageSize?: number;
  };
  filters?: Record<
    string | number,
    string | number | boolean | NestedFilter | NestedFilter[]
  >;
  sort?: string[] | number[];
}

interface FilterObject {
  [key: string]: FilterValue;
}

type FilterValue = string | number | boolean | FilterObject | FilterValue[];

function processFilters(
  filters: Record<string, FilterValue>,
  params: URLSearchParams,
  prefix = "filters"
): void {
  Object.entries(filters).forEach(([key, value]) => {
    const currentKey = prefix ? `${prefix}[${key}]` : key;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === "object" && item !== null) {
          processFilters(
            item as Record<string, FilterValue>,
            params,
            `${currentKey}[${index}]`
          );
        } else {
          params.append(`${currentKey}[${index}]`, String(item));
        }
      });
    } else if (typeof value === "object" && value !== null) {
      processFilters(value, params, currentKey);
    } else {
      params.append(currentKey, String(value));
    }
  });
}

function processPopulate(
  populate: string | string[] | Record<string, string[]>,
  params: URLSearchParams
): void {
  if (populate === "*") {
    params.append("populate", "*");
  } else if (typeof populate === "string") {
    params.append("populate", populate);
  } else if (Array.isArray(populate)) {
    populate.forEach((field) => {
      params.append("populate[]", field);
    });
  } else if (typeof populate === "object") {
    // If populate is an object with nested fields
    let counter = 1;
    Object.entries(populate).forEach(([key, values]) => {
      // Add the main key itself
      params.append(`populate[${counter}]`, key);
      counter++;

      // Add each nested field as populate[<counter>]=key.field
      values.forEach((value) => {
        const prefixedValue = `${key}.${value}`;
        params.append(`populate[${counter}]`, prefixedValue);
        counter++;
      });
    });
  }
}

export function buildQueryParams(
  basePath: string = "",
  options: QueryOptions
): string {
  const params = new URLSearchParams();

  // Handle populate
  if (options.populate) {
    processPopulate(options.populate, params);
  }

  // Handle fields
  if (options?.fields) {
    options?.fields.forEach((value, index) => {
      if (value !== undefined) {
        params.append(`fields[${index}]`, value.toString());
      }
    });
  }

  // Handle pagination
  if (options?.pagination) {
    Object.entries(options.pagination).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(`pagination[${key}]`, value.toString());
      }
    });
  }

  // Handle filters
  if (options.filters) {
    processFilters(options.filters, params);
  }

  // Handle sorting
  if (options?.sort) {
    options?.sort.forEach((value, index) => {
      if (value !== undefined) {
        params.append(`sort[${index}]`, value.toString());
      }
    });
  }

  const queryString = params.toString();

  return basePath
    ? `${basePath}${queryString ? `?${queryString}` : ""}`
    : queryString;
}
